import has from 'lodash/has';
import get from 'lodash/get';
import { isEmpty } from 'lodash';
import einsteinRecommendationQuery from '@tcp/core/src/queries/einteinRecommendation.query.graphql';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import { executeStatefulGraphQLQuery } from '../../../handler';
import { getAPIConfig, isCanada } from '../../../../utils';
import { getPdpUrl } from '../../../../components/features/browse/PlpTile/views/plpTileHelpers/plpTileCommonHelpers';
import { executeExternalAPICall } from '../../../handler/handler';
import { getAlternateBrandName } from '../../../../components/features/browse/SearchDetail/container/SearchDetail.selectors';
import { getIsOrderableFilterEnable } from '../../../../reduxStore/selectors/session.selectors';
import { getSimilarProductInfo } from '../../productListing/productDetail';
import { FIELD_LIST } from '../../../../components/common/molecules/AiRecommendations/container/AiRecommendations.constants';

const getImageUrl = (prodId, prodImage) => {
  if (isEmpty(prodId)) return null;

  const basePart = prodId.split('_')[0];
  return `${basePart}/${prodImage}`;
};

function prepareCarouselData(response, strategy) {
  const categoriesList = [
    {
      id: strategy,
    },
  ];
  if (Array.isArray(response) && !response.length) return { categoriesList, recommendation: [] };
  return {
    categoriesList,
    [strategy]: response?.map((item) => {
      const {
        product: {
          ProductImage,
          TCPBazaarVoiceRating,
          TCPBazaarVoiceReviewCount,
          TCPMerchantTagCanadaStore,
          TCPMerchantTagUSStore,
          TCPPrimaryBrandCanadaStore,
          TCPPrimaryBrandUSStore,
          TCPStyleQTYCA,
          TCPStyleQTYUS,
          TCPStyleTypeCA,
          TCPStyleTypeUS,
          seotoken: seoToken,
        },
        price,
        salePrice,
        itemGroupId: uniqueId,
        title,
      } = item;

      const primaryBrand = isCanada() ? TCPPrimaryBrandCanadaStore : TCPPrimaryBrandUSStore;
      const apiConfig = getAPIConfig();
      const { siteId } = apiConfig;

      const pdpUrlObj = getPdpUrl({
        seoToken,
        siteId,
        productPartNo: uniqueId,
        isBundleProduct: false,
        TCPPrimaryCategory: undefined,
      });

      return {
        uniqueId,
        productInfo: {
          primaryBrand,
          icidParam: '',
          pdpUrl: pdpUrlObj.pdpUrl.asPathWithoutSlug,
          name: title,
          imageUrl: getImageUrl(uniqueId, ProductImage),
          offerPrice: salePrice,
          listPrice: price,
          ratings: TCPBazaarVoiceRating,
          TCPMerchantTagUSStore,
          TCPMerchantTagCanadaStore,
          reviewsCount: TCPBazaarVoiceReviewCount,
          tcpStyleType: isCanada() ? TCPStyleTypeCA : TCPStyleTypeUS,
          tcpStyleQty: isCanada() ? TCPStyleQTYCA : TCPStyleQTYUS,
        },
      };
    }),
  };
}

export function parseAiResponse(response, strategy) {
  try {
    const {
      recommendationsInfo: { items = [] },
    } = response;
    const parsedResponse = items?.map((item) => {
      if (has(item, 'description')) {
        const attributesList = get(item, 'description')?.split(';');
        const product = attributesList?.reduce((acc, val) => {
          const [key, value] = val?.split(':');
          acc[key] = value;
          return acc;
        }, {});
        return { product, ...item };
      }
      return item;
    });
    return prepareCarouselData(parsedResponse, strategy);
  } catch (error) {
    throw Error('Error parsing the API response');
  }
}

export async function getEinsteinRecommendations({ productId, strategy }) {
  const payload = {
    productIds: productId,
    recommenderId: strategy,
  };
  return executeStatefulGraphQLQuery({
    query: einsteinRecommendationQuery,
    type: 'query',
    variables: payload,
  }).then((res) => {
    if (!res.data) {
      throw Error('Einstein API not returning data');
    }
    return parseAiResponse(res.data, strategy);
  });
}

async function getSimilarProductsData(similarProductIds, xappURLConfig, brand, strategy) {
  try {
    const state = getStoreRef().getState();
    const alternateBrand = getAlternateBrandName(state);
    const apiConfig = getAPIConfig();
    const isUNBXDOrderableFlag = getIsOrderableFilterEnable(state);
    const response = await getSimilarProductInfo({
      productIds: similarProductIds,
      fields: FIELD_LIST.join(','),
      brand,
      alternateBrand,
      xappURLConfig,
      originalURL: window.location.href,
      apiConfig,
      isUNBXDOrderableFlag,
      state,
    });
    const parseGenAiData = response?.map((item) => {
      const {
        TCPBazaarVoiceRating,
        TCPBazaarVoiceReviewCount,
        TCPMerchantTagCanadaStore,
        TCPMerchantTagUSStore,
        TCPPrimaryBrandCanadaStore,
        TCPPrimaryBrandUSStore,
        TCPStyleQTYCA,
        TCPStyleQTYUS,
        TCPStyleTypeCA,
        TCPStyleTypeUS,
        seo_token: seotoken,
        uniqueId,
        min_list_price: price,
        min_offer_price: salePrice,
        productimage,
        product_name: title,
      } = item;
      return {
        product: {
          TCPBazaarVoiceRating,
          TCPBazaarVoiceReviewCount,
          TCPMerchantTagCanadaStore,
          TCPMerchantTagUSStore,
          TCPPrimaryBrandCanadaStore,
          TCPPrimaryBrandUSStore,
          TCPStyleQTYCA,
          TCPStyleQTYUS,
          TCPStyleTypeCA,
          TCPStyleTypeUS,
          seotoken,
          ProductImage: productimage,
        },
        price,
        salePrice,
        itemGroupId: uniqueId,
        title,
      };
    });
    return prepareCarouselData(parseGenAiData, strategy);
  } catch (error) {
    throw new Error(`Error fetching similarProducts API - ${error}`);
  }
}

function parseGenAiApiResponse(response, xappURLConfig, brand, strategy) {
  try {
    if (has(response, 'similar_products')) {
      const similarProductIds = get(response, 'similar_products')
        ?.map((items) => items[0])
        .join(',');
      return getSimilarProductsData(similarProductIds, xappURLConfig, brand, strategy);
    }
    return {};
  } catch (error) {
    throw new Error(`Error parsing genAi API response - ${error}`);
  }
}

export async function getGenAiRecommendations({
  productId,
  brand,
  gender,
  xappURLConfig,
  strategy,
}) {
  const { genAiSimilarProductsUrl } = getAPIConfig();
  let url = `${genAiSimilarProductsUrl}?product_id=${productId}&brand=${brand}`;
  if (gender) {
    url += `&gender=${gender}`;
  }
  const reqObject = {
    webService: { URI: url, method: 'GET' },
  };
  return executeExternalAPICall(reqObject)
    .then((res) => {
      return parseGenAiApiResponse(res?.body, xappURLConfig, brand, strategy);
    })
    .catch((err) => {
      throw new Error(`Error occurred in getGenAiRecommendations API - \n ${err}`);
    });
}
